import type React from 'react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { useIsCurrentLink, useIsCurrentLinkCallBack } from '@/hooks'
import * as Accordion from '@radix-ui/react-accordion'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'

import { Paragraph } from '../Typography'
import { menuItemStyles } from './styles'

const {
	MenuItemStitchesComponent,
	IconContainer,
	StyledChevron,
	ActiveIndicator,
	ContainerMenuAccordion,
	ScrollableSection,
} = menuItemStyles

type MenuItemStitchesComponentProps = React.ComponentProps<
	typeof MenuItemStitchesComponent
>

type ItemsProps = {
	route: string
	name: string | ReactNode
	icon: ReactNode
	externalLink?: boolean
	tourId?: string
}

interface MenuItemProps extends MenuItemStitchesComponentProps {
	name: string | ReactNode
	icon: ReactNode
	isOpened: boolean
	externalLink?: boolean
	items?: Array<ItemsProps>
	isButton?: boolean
}

const MenuItem = ({
	to,
	name,
	icon,
	isOpened,
	items,
	isButton,
	externalLink = false,
	...rest
}: MenuItemProps) => {
	const [zoom, setZoom] = useState(100)
	const [scroll, setScroll] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)
	const [isAccordionOpen, setIsAccordionOpen] = useState(false)

	const { isCurrent } = useIsCurrentLink(to as string)
	const { executeCurrentLink } = useIsCurrentLinkCallBack()

	const isCurrentCallback = useCallback(() => {
		if (items) {
			const matches = items.map((content: ItemsProps) => {
				return executeCurrentLink(content.route)
			})

			return matches.find((current) => current)
		}

		return false
	}, [items, executeCurrentLink])

	const isCurrentAccordion = isCurrent || isCurrentCallback()

	useEffect(() => {
		setIsAccordionOpen(!!isCurrentAccordion)
	}, [isCurrentAccordion])

	useEffect(() => {
		function getSizes() {
			const currentZoom = Math.round(
				((window.outerWidth - 10) / window.innerWidth) * 100
			)

			setZoom(currentZoom)
		}

		function getHeight() {
			const screenHeight = window.innerHeight < 650

			setScroll(screenHeight)
		}

		window.addEventListener('resize', getSizes, false)
		window.addEventListener('resize', getHeight, false)

		getSizes()
		getHeight()

		return () => {
			window.removeEventListener('resize', getSizes, false)
			window.removeEventListener('resize', getHeight, false)
		}
	}, [])

	return (
		<>
			{items && items.length > 0 ? (
				<Accordion.Root
					type='single'
					collapsible
					value={isAccordionOpen ? name?.toString() || `item-${to}` : undefined}
					onValueChange={(value) => setIsAccordionOpen(!!value)}
				>
					<Accordion.Item value={name?.toString() || `item-${to}`}>
						<ContainerMenuAccordion
							isActive={isCurrent}
							isOpened={isOpened}
							isOpen={isCurrentAccordion}
							css={{ flexDirection: 'column', alignItems: 'stretch' }}
						>
							<Accordion.Trigger asChild>
								<MenuItemStitchesComponent
									{...rest}
									to={to}
									isActive={isCurrent}
									isOpened={isOpened}
								>
									<IconContainer isActive={isCurrentAccordion}>
										{icon}
									</IconContainer>
									{isOpened &&
										(isCurrentAccordion ? (
											<Paragraph
												paragraphType='p1'
												css={{
													color: '$primary',
													fontWeight: '$bold',
													flex: 1,
												}}
											>
												{name}
											</Paragraph>
										) : (
											<Paragraph
												paragraphType='p1'
												css={{ color: '$neutral90', flex: 1 }}
											>
												{name}
											</Paragraph>
										))}
									{isOpened && (
										<StyledChevron isActive={isCurrentAccordion}>
											{isAccordionOpen ? (
												<ChevronUpIcon />
											) : (
												<ChevronDownIcon />
											)}
										</StyledChevron>
									)}
									{isCurrent && <ActiveIndicator />}
								</MenuItemStitchesComponent>
							</Accordion.Trigger>
							<Accordion.Content
								style={{
									overflow: 'hidden',
									transition: 'max-height 0.3s ease-in-out',
									maxHeight:
										isAccordionOpen && contentRef.current
											? `${contentRef.current.scrollHeight}px`
											: '0',
								}}
							>
								<ScrollableSection
									ref={contentRef}
									zoomed={zoom > 130 || scroll}
									css={{
										display: 'flex',
										flexDirection: 'column',
										padding: '8px 0',
									}}
								>
									{items.map(
										(
											{ name: nameItem, icon: iconItem, route, tourId },
											index
										) => {
											const isCurrentItem = executeCurrentLink(route)

											return (
												<MenuItemStitchesComponent
													{...rest}
													to={route}
													isActive={isCurrentItem}
													isOpened={isOpened}
													isAccordionItem
													key={index}
													className={tourId}
												>
													<IconContainer isActive={isCurrentItem}>
														{iconItem}
													</IconContainer>
													{isOpened &&
														(isCurrentItem ? (
															<Paragraph
																paragraphType='p1'
																css={{
																	color: '$primary',
																	fontWeight: '$bold',
																}}
															>
																{nameItem}
															</Paragraph>
														) : (
															<Paragraph
																paragraphType='p1'
																css={{ color: '$neutral90' }}
															>
																{nameItem}
															</Paragraph>
														))}
													{isCurrentItem && <ActiveIndicator />}
												</MenuItemStitchesComponent>
											)
										}
									)}
								</ScrollableSection>
							</Accordion.Content>
						</ContainerMenuAccordion>
					</Accordion.Item>
				</Accordion.Root>
			) : (
				<MenuItemStitchesComponent
					{...rest}
					{...(!isButton && !externalLink && to ? { to } : {})}
					isActive={isCurrent}
					isOpened={isOpened}
					{...(!isButton && externalLink && to
						? {
								as: 'a',
								href: to as string,
								target: '_blank',
								rel: 'noopener noreferrer',
						  }
						: {})}
				>
					<IconContainer isActive={isCurrent}>{icon}</IconContainer>
					{isOpened &&
						(isCurrent ? (
							<Paragraph
								paragraphType='p1'
								css={{ color: '$primary', fontWeight: '$bold' }}
							>
								{name}
							</Paragraph>
						) : (
							<Paragraph paragraphType='p1' css={{ color: '$neutral90' }}>
								{name}
							</Paragraph>
						))}
					{isCurrent && <ActiveIndicator />}
				</MenuItemStitchesComponent>
			)}
		</>
	)
}

export { MenuItem }
